import { useEffect, useMemo, useState } from "react";
import { Loader2, Plus, HeartPulse } from "lucide-react";
import { Button } from "@project/components/ui/button";
import { Checkbox } from "@project/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@project/components/ui/dialog";
import { Input } from "@project/components/ui/input";
import { Label } from "@project/components/ui/label";
import { Textarea } from "@project/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@project/components/ui/select";
import { MobileNumberField } from "@project/components/mobile-number-field";
import { getApiErrorMessage } from "@project/lib/api-error";
import { projectToast } from "@project/lib/toast";
import { healthcareDonationService } from "@project/services/admin/healthcare-donation.service";
import { HEALTHCARE_CATEGORIES } from "@project/user/components/healthcare-donation-modal";
import type { HealthcareAddDonationPayload } from "@project/types/healthcare-donation";

type FormState = {
  category: string;
  sub_category: string;
  amount: string;
  donor_name: string;
  mobile_number: string;
  email: string;
  city: string;
  full_address: string;
  state: string;
  pincode: string;
  country: string;
  occasion: string;
  display_name: string;
  special_instructions: string;
  is_anonymous: boolean;
  info_confirmed: boolean;
  contact_consent: boolean;
  payment_mode: "cash" | "cheque" | "neft" | "rtgs" | "imps" | "upi";
  payment_reference: string;
};

const initialForm: FormState = {
  category: "Medical Clinic",
  sub_category: "",
  amount: "",
  donor_name: "",
  mobile_number: "",
  email: "",
  city: "Ahmedabad",
  full_address: "",
  state: "Gujarat",
  pincode: "",
  country: "India",
  occasion: "Medical Support",
  display_name: "",
  special_instructions: "",
  is_anonymous: false,
  info_confirmed: true,
  contact_consent: true,
  payment_mode: "cash",
  payment_reference: "",
};

type HealthcareAddDonationDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
};

export function HealthcareAddDonationDialog({
  open,
  onOpenChange,
  onSuccess,
}: HealthcareAddDonationDialogProps) {
  const [form, setForm] = useState<FormState>(initialForm);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      setForm(initialForm);
      setSubmitting(false);
    }
  }, [open]);

  const patch = (updates: Partial<FormState>) =>
    setForm(prev => ({ ...prev, ...updates }));

  const subCategories = useMemo(() => {
    const selected = HEALTHCARE_CATEGORIES.find(
      item => item.category === form.category
    );
    return selected ? selected.sub_categories : [];
  }, [form.category]);

  // Sync sub-category selection
  useEffect(() => {
    if (
      subCategories.length > 0 &&
      !subCategories.includes(form.sub_category)
    ) {
      setForm(prev => ({ ...prev, sub_category: subCategories[0] }));
    }
  }, [subCategories, form.sub_category]);

  const buildPayload = (): HealthcareAddDonationPayload => {
    if (!form.category) {
      throw new Error("Please select a donation category.");
    }
    if (!form.sub_category) {
      throw new Error("Please select a donation sub-category.");
    }
    const amtParsed = parseFloat(form.amount);
    if (Number.isNaN(amtParsed) || amtParsed <= 0) {
      throw new Error("Please enter a valid donation amount.");
    }
    if (!form.donor_name.trim()) {
      throw new Error("Donor name is required.");
    }
    if (!/^\d{10}$/.test(form.mobile_number.trim())) {
      throw new Error("Enter a valid 10-digit mobile number.");
    }
    if (!form.email.trim()) {
      throw new Error("Email is required.");
    }
    if (!form.city.trim()) {
      throw new Error("City is required.");
    }
    if (!form.full_address.trim()) {
      throw new Error("Full address is required.");
    }
    if (!form.state.trim()) {
      throw new Error("State is required.");
    }
    if (!/^\d{6}$/.test(form.pincode.trim())) {
      throw new Error("Enter a valid 6-digit pincode.");
    }
    if (!form.payment_mode) {
      throw new Error("Payment mode is required.");
    }
    if (!form.payment_reference.trim()) {
      throw new Error("Payment reference is required.");
    }
    if (!form.info_confirmed) {
      throw new Error("Please confirm the donation information.");
    }

    return {
      category: form.category,
      sub_category: form.sub_category,
      amount: amtParsed.toFixed(2),
      donor_name: form.donor_name.trim(),
      mobile_number: form.mobile_number.trim(),
      email: form.email.trim(),
      city: form.city.trim(),
      full_address: form.full_address.trim(),
      state: form.state.trim(),
      pincode: form.pincode.trim(),
      country: form.country.trim() || "India",
      occasion: form.occasion.trim() || undefined,
      display_name: form.display_name.trim() || undefined,
      special_instructions: form.special_instructions.trim() || undefined,
      is_anonymous: form.is_anonymous,
      info_confirmed: form.info_confirmed,
      contact_consent: form.contact_consent,
      mode: "offline",
      payment_mode: form.payment_mode,
      payment_reference: form.payment_reference.trim(),
    };
  };

  const handleSubmit = async () => {
    try {
      const payload = buildPayload();
      setSubmitting(true);
      await healthcareDonationService.add(payload);
      projectToast.success("Healthcare donation added successfully");
      onOpenChange(false);
      onSuccess();
    } catch (error) {
      projectToast.error(getApiErrorMessage(error, "Could not add donation"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[92vh] w-[calc(100vw-1.5rem)] max-w-3xl flex-col gap-0 overflow-hidden rounded-2xl p-0">
        <DialogHeader className="shrink-0 border-b border-border px-5 py-4 pr-12 text-left sm:px-6">
          <div className="flex items-start gap-3">
            <div className="bg-primary/10 grid h-10 w-10 shrink-0 place-items-center rounded-xl text-primary">
              <HeartPulse className="h-5 w-5" />
            </div>
            <div>
              <DialogTitle className="text-lg font-semibold sm:text-xl">
                Add Healthcare Donation
              </DialogTitle>
              <p className="mt-0.5 text-sm text-muted-foreground">
                Record an offline medical/clinic donation manually
              </p>
            </div>
          </div>
        </DialogHeader>

        <div className="min-h-0 flex-1 space-y-5 overflow-y-auto px-5 py-5 sm:px-6">
          <section className="grid gap-3 sm:grid-cols-2">
            <div>
              <Label data-required="true">Category</Label>
              <Select
                value={form.category}
                onValueChange={category =>
                  patch({ category, sub_category: "" })
                }
              >
                <SelectTrigger className="mt-1 rounded-xl">
                  <SelectValue placeholder="Select Category" />
                </SelectTrigger>
                <SelectContent>
                  {HEALTHCARE_CATEGORIES.map(item => (
                    <SelectItem key={item.category} value={item.category}>
                      {item.category}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label data-required="true">Sub-Category</Label>
              <Select
                value={form.sub_category}
                onValueChange={sub_category => patch({ sub_category })}
              >
                <SelectTrigger className="mt-1 rounded-xl">
                  <SelectValue placeholder="Select Sub-Category" />
                </SelectTrigger>
                <SelectContent>
                  {subCategories.map(sub => (
                    <SelectItem key={sub} value={sub}>
                      {sub}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label data-required="true">Amount (₹)</Label>
              <Input
                type="number"
                min={0}
                step="0.01"
                value={form.amount}
                onChange={e => patch({ amount: e.target.value })}
                placeholder="5000"
                className="mt-1 rounded-xl"
              />
            </div>

            <div>
              <Label data-required="true">Payment Mode</Label>
              <Select
                value={form.payment_mode}
                onValueChange={value => patch({ payment_mode: value as any })}
              >
                <SelectTrigger className="mt-1 rounded-xl">
                  <SelectValue placeholder="Select payment mode..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="cash">Cash</SelectItem>
                  <SelectItem value="cheque">Cheque</SelectItem>
                  <SelectItem value="neft">NEFT</SelectItem>
                  <SelectItem value="rtgs">RTGS</SelectItem>
                  <SelectItem value="imps">IMPS</SelectItem>
                  <SelectItem value="upi">UPI</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label data-required="true">Payment Reference</Label>
              <Input
                value={form.payment_reference}
                onChange={e => patch({ payment_reference: e.target.value })}
                placeholder="E.g., CASH-001 or Cheque no / transaction id"
                className="mt-1 rounded-xl"
              />
            </div>

            <div>
              <Label>Occasion</Label>
              <Input
                value={form.occasion}
                onChange={e => patch({ occasion: e.target.value })}
                placeholder="E.g. Medical Support, Birthday, In Memory of..."
                className="mt-1 rounded-xl"
              />
            </div>
          </section>

          <section>
            <h3 className="mb-3 font-serif text-lg font-semibold text-foreground">
              Donor Details
            </h3>
            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <Label data-required="true">Donor Name</Label>
                <Input
                  value={form.donor_name}
                  onChange={e => patch({ donor_name: e.target.value })}
                  placeholder="Anmol"
                  className="mt-1 rounded-xl"
                />
              </div>
              <div>
                <Label>Display Name</Label>
                <Input
                  value={form.display_name}
                  onChange={e => patch({ display_name: e.target.value })}
                  placeholder="Anmol"
                  className="mt-1 rounded-xl"
                />
              </div>
              <div>
                <Label data-required="true">Mobile Number</Label>
                <MobileNumberField
                  required
                  value={form.mobile_number}
                  onChange={mobile_number => patch({ mobile_number })}
                  placeholder="10-digit phone number"
                  className="mt-1 rounded-xl"
                />
              </div>
              <div>
                <Label data-required="true">Email</Label>
                <Input
                  type="email"
                  value={form.email}
                  onChange={e => patch({ email: e.target.value })}
                  placeholder="test@example.com"
                  className="mt-1 rounded-xl"
                />
              </div>
              <div className="sm:col-span-2">
                <Label data-required="true">Full Address</Label>
                <Textarea
                  value={form.full_address}
                  onChange={e => patch({ full_address: e.target.value })}
                  placeholder="ABC Address"
                  className="mt-1 rounded-xl"
                  rows={2}
                />
              </div>
              <div>
                <Label data-required="true">City</Label>
                <Input
                  value={form.city}
                  onChange={e => patch({ city: e.target.value })}
                  placeholder="Ahmedabad"
                  className="mt-1 rounded-xl"
                />
              </div>
              <div>
                <Label data-required="true">State</Label>
                <Input
                  value={form.state}
                  onChange={e => patch({ state: e.target.value })}
                  placeholder="Gujarat"
                  className="mt-1 rounded-xl"
                />
              </div>
              <div>
                <Label data-required="true">Pincode</Label>
                <Input
                  value={form.pincode}
                  onChange={e => patch({ pincode: e.target.value })}
                  placeholder="380001"
                  maxLength={6}
                  className="mt-1 rounded-xl"
                />
              </div>
              <div>
                <Label>Country</Label>
                <Input
                  value={form.country}
                  onChange={e => patch({ country: e.target.value })}
                  placeholder="India"
                  className="mt-1 rounded-xl"
                />
              </div>
              <div className="sm:col-span-2">
                <Label>Special Instructions</Label>
                <Textarea
                  value={form.special_instructions}
                  onChange={e =>
                    patch({ special_instructions: e.target.value })
                  }
                  placeholder="Optional notes"
                  className="mt-1 rounded-xl"
                  rows={2}
                />
              </div>
            </div>
          </section>

          <section className="bg-muted/20 space-y-3 rounded-xl border border-border p-4">
            <label className="flex cursor-pointer items-start gap-3">
              <Checkbox
                checked={form.is_anonymous}
                onCheckedChange={checked =>
                  patch({ is_anonymous: checked === true })
                }
                className="mt-0.5"
              />
              <span className="text-sm text-foreground">
                Record as anonymous donation
              </span>
            </label>
            <label className="flex cursor-pointer items-start gap-3">
              <Checkbox
                checked={form.info_confirmed}
                onCheckedChange={checked =>
                  patch({ info_confirmed: checked === true })
                }
                className="mt-0.5"
              />
              <span className="text-sm text-foreground">
                Information confirmed{" "}
                <span className="text-destructive">*</span>
              </span>
            </label>
            <label className="flex cursor-pointer items-start gap-3">
              <Checkbox
                checked={form.contact_consent}
                onCheckedChange={checked =>
                  patch({ contact_consent: checked === true })
                }
                className="mt-0.5"
              />
              <span className="text-sm text-foreground">
                Contact consent for this sponsorship
              </span>
            </label>
          </section>
        </div>

        <DialogFooter className="shrink-0 gap-2 border-t border-border px-5 py-4 sm:px-6">
          <Button
            type="button"
            variant="outline"
            className="rounded-xl"
            disabled={submitting}
            onClick={() => onOpenChange(false)}
          >
            Cancel
          </Button>
          <Button
            type="button"
            className="gap-1.5 rounded-xl font-semibold"
            disabled={submitting}
            onClick={() => void handleSubmit()}
          >
            {submitting ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Plus className="h-4 w-4" />
            )}
            {submitting ? "Saving…" : "Add Donation"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
